<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { Letter } from 'vue-letter';
import { allowedCssProperties } from 'lettersanitizer';

import Icon from 'next/icon/Icon.vue';
import { prepareEmailDisplay } from 'dashboard/helper/emailDisplayContent.js';
import FormattedContent from 'next/message/bubbles/Text/FormattedContent.vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import MessageMeta from 'next/message/MessageMeta.vue';
import EmailOpenSignal from 'next/message/EmailOpenSignal.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import EmailDetailsPopover from './EmailDetailsPopover.vue';

import { useMessageContext } from '../../provider.js';
import { MESSAGE_TYPES } from 'next/message/constants.js';
import { useTranslations } from 'dashboard/composables/useTranslations';
import { useMessageTranslation } from 'dashboard/composables/useMessageTranslation';
import { useTranslationExpansion } from 'dashboard/composables/useTranslationExpansion';
import {
  useChineseDisplay,
  queueMissingTranslation,
} from 'dashboard/composables/useChineseDisplay';

const context = useMessageContext();
const aiLabel = 'AI';
const { content, contentAttributes, attachments, messageType } = context;

const showQuotedMessage = ref(false);
const renderRevision = ref(0);

const isOutgoing = computed(() => messageType.value === MESSAGE_TYPES.OUTGOING);
const isIncoming = computed(() => !isOutgoing.value);

const emailDisplay = computed(() =>
  prepareEmailDisplay({
    email: contentAttributes.value?.email || {},
    text: content.value,
  })
);
const translationSourceReady = computed(
  () => emailDisplay.value.ready && emailDisplay.value.hasReplyText
);
const translationSourceText = computed(() => emailDisplay.value.replyText);
const emptyReplyNotice = computed(() =>
  attachments.value?.length
    ? '邮件包含附件，未提取到新的回复正文。'
    : '未提取到新的回复正文。'
);
const translationSourceKey = computed(() =>
  JSON.stringify([
    emailDisplay.value.status,
    emailDisplay.value.parserVersion,
    emailDisplay.value.sourceDigest,
  ])
);

const { translationContent: preferredTranslation } =
  useTranslations(contentAttributes);
const cachedTranslation = computed(() => {
  const attributes = contentAttributes.value || {};
  return attributes.borderchatTranslation || attributes.borderchat_translation
    ? attributes.translations?.zh_CN || null
    : preferredTranslation.value;
});
const {
  eligible: canTranslate,
  state: translationState,
  translation: translationContent,
  pending: translationPending,
  showRequest: showTranslationRequest,
  canRequest: canRequestTranslation,
  notice: translationNotice,
  requestLabel: translationRequestLabel,
  requestTranslation,
} = useMessageTranslation(context, cachedTranslation, {
  sourceReady: translationSourceReady,
  sourceText: translationSourceText,
  sourceKey: translationSourceKey,
});
const { translationExpanded, expandTranslation, toggleTranslation } =
  useTranslationExpansion(context, translationContent, {
    sourceDigest: computed(() => emailDisplay.value.sourceDigest),
  });
const translateMessage = async () => {
  if (!canRequestTranslation.value) return;
  expandTranslation();
  await requestTranslation();
};
const visibleTranslation = computed(() => translationContent.value || '');
const hasVisibleTranslation = computed(
  () =>
    canTranslate.value &&
    emailDisplay.value.hasReplyText &&
    visibleTranslation.value.trim() &&
    visibleTranslation.value.trim() !== emailDisplay.value.replyText.trim()
);
// With Chinese display on, the reply shows only its translation; expanding the
// quoted text still shows the full original email.
const { showChinese } = useChineseDisplay();
const showsChinese = computed(
  () =>
    showChinese.value && hasVisibleTranslation.value && !showQuotedMessage.value
);
// The reply's pictures still show under its translation (2026-10-14): those of
// its HTML that can load here (http(s) or an inline image), tracking pixels
// (a width or height under 3) left out.
const PICTURE_SOURCE = /^(https?:\/\/|data:image\/)/i;
const chinesePictures = computed(() => {
  const html = showsChinese.value ? emailDisplay.value.replyHTML || '' : '';
  if (!/<img/i.test(html)) return [];
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const seen = new Set();
  return [...doc.querySelectorAll('img')].flatMap(img => {
    const src = (img.getAttribute('src') || '').trim();
    const tiny = ['width', 'height'].some(name => {
      const size = parseInt(img.getAttribute(name), 10);
      return Number.isFinite(size) && size < 3;
    });
    if (tiny || seen.has(src) || !PICTURE_SOURCE.test(src)) return [];
    seen.add(src);
    return [
      { src, alt: img.getAttribute('alt') || '', linked: !/^data:/i.test(src) },
    ];
  });
});
// A reply never translated is queued once for the background translation.
const route = useRoute();
const autoQueued = ref(false);
const neverTranslated = computed(
  () => translationState?.value?.status === 'untranslated'
);
watch(
  () => showChinese.value && canTranslate.value && neverTranslated.value,
  wanted => {
    if (
      wanted &&
      queueMissingTranslation(
        route?.params?.accountId,
        context.conversationId?.value,
        context.id?.value
      )
    ) {
      autoQueued.value = true;
    }
  },
  { immediate: true }
);
const displayNotice = computed(
  () =>
    translationNotice.value ||
    (autoQueued.value && showChinese.value && neverTranslated.value
      ? '正在翻译…'
      : '')
);

// Letter sanitizes once on mount; remount only when display inputs change.
watch(
  [
    () => emailDisplay.value.fullHTML,
    () => emailDisplay.value.replyHTML,
    () => translationSourceKey.value,
  ],
  () => {
    showQuotedMessage.value = false;
    renderRevision.value += 1;
  }
);

// Forwarded in by a trusted forwarder: the conversation belongs to the original customer.
const forwardInfo = computed(() => {
  const email = contentAttributes.value?.email || {};
  const forward = email.borderchatForward || email.borderchat_forward;
  const by = forward?.forwardedBy || forward?.forwarded_by;
  return by ? { by, note: forward.note || '' } : null;
});

const hasEmailContent = computed(() => {
  return (
    contentAttributes?.value?.email?.textContent?.full ||
    contentAttributes?.value?.email?.htmlContent?.full
  );
});

const displayHTML = computed(() =>
  showQuotedMessage.value
    ? emailDisplay.value.fullHTML
    : emailDisplay.value.replyHTML
);
</script>

<template>
  <div
    class="w-full flex flex-col"
    :class="{
      'items-start': isIncoming,
      'items-end': isOutgoing,
    }"
  >
    <MessageMeta class="mb-2 text-n-slate-11">
      <template #afterTime>
        <EmailDetailsPopover />
      </template>
    </MessageMeta>
    <div
      class="email-translation-row min-w-0 w-fit"
      :class="{ 'flex-row-reverse': isOutgoing }"
    >
      <BaseBubble
        hide-meta
        class="!w-fit min-w-[180px] !max-w-full overflow-hidden"
        :class="{
          'bg-n-slate-4': isIncoming,
          'bg-n-solid-blue': isOutgoing,
          'ms-auto': isOutgoing,
        }"
        data-bubble-name="email"
      >
        <section
          v-if="
            !emailDisplay.hasReplyText &&
            Array.isArray(attachments) &&
            attachments.length
          "
          class="px-4 pt-3 space-y-2"
        >
          <AttachmentChips :attachments="attachments" class="gap-1" />
        </section>
        <section class="min-w-0 px-3.5 py-2.5">
          <p
            v-if="forwardInfo"
            class="mb-2 text-xs text-n-slate-11 whitespace-pre-line"
            data-trusted-forward
          >
            {{ `由 ${forwardInfo.by} 转发`
            }}{{ forwardInfo.note ? `；附言：${forwardInfo.note}` : '' }}
          </p>
          <div class="min-w-0 break-words overflow-x-auto">
            <FormattedContent
              v-if="
                isOutgoing && content && !hasEmailContent && !emailDisplay.ready
              "
              class="text-n-slate-12"
              :content="content"
            />
            <FormattedContent
              v-else-if="showsChinese"
              class="text-n-slate-12"
              :content="visibleTranslation"
              data-chinese-display
            />
            <Letter
              v-else
              :key="`${renderRevision}-${showQuotedMessage}`"
              class-name="prose prose-bubble !max-w-none letter-render leading-relaxed"
              :html="displayHTML"
              :allowed-css-properties="[
                ...allowedCssProperties,
                'transform',
                'transform-origin',
              ]"
            />
            <div
              v-if="chinesePictures.length"
              class="flex flex-wrap gap-2 mt-2"
              data-chinese-pictures
            >
              <template v-for="picture in chinesePictures" :key="picture.src">
                <a
                  v-if="picture.linked"
                  :href="picture.src"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="block"
                >
                  <img
                    :src="picture.src"
                    :alt="picture.alt"
                    loading="lazy"
                    class="block max-h-60 max-w-full rounded-md"
                  />
                </a>
                <img
                  v-else
                  :src="picture.src"
                  :alt="picture.alt"
                  loading="lazy"
                  class="block max-h-60 max-w-full rounded-md"
                />
              </template>
            </div>
            <p
              v-if="
                emailDisplay.ready &&
                !emailDisplay.hasReplyContent &&
                !showQuotedMessage
              "
              class="mb-0 text-sm text-n-slate-11"
            >
              {{ emptyReplyNotice }}
            </p>
            <p
              v-if="emailDisplay.notice"
              class="mb-0 mt-2 text-xs text-n-slate-11"
              role="status"
            >
              {{ emailDisplay.notice }}
            </p>
            <button
              v-if="emailDisplay.hasQuotes"
              type="button"
              :aria-expanded="showQuotedMessage"
              class="text-n-slate-11 px-1 leading-none text-sm bg-n-alpha-black2 text-center flex items-center gap-1 mt-2"
              @click="showQuotedMessage = !showQuotedMessage"
            >
              <template v-if="showQuotedMessage">
                {{ $t('CHAT_LIST.HIDE_QUOTED_TEXT') }}
              </template>
              <template v-else>
                {{ $t('CHAT_LIST.SHOW_QUOTED_TEXT') }}
              </template>
              <Icon
                :icon="
                  showQuotedMessage
                    ? 'i-lucide-chevron-up'
                    : 'i-lucide-chevron-down'
                "
              />
            </button>
          </div>
        </section>
        <section
          v-if="hasVisibleTranslation && translationExpanded && !showsChinese"
          class="min-w-0 break-words px-3.5 py-2.5 border-t-2 border-n-slate-8"
        >
          <FormattedContent :content="visibleTranslation" />
        </section>
        <section
          v-if="
            emailDisplay.hasReplyText &&
            Array.isArray(attachments) &&
            attachments.length
          "
          class="px-4 pb-4 space-y-2"
        >
          <AttachmentChips :attachments="attachments" class="gap-1" />
        </section>
        <section
          v-if="canTranslate && displayNotice"
          class="px-3.5 pb-2.5 text-xs text-n-slate-11"
          role="status"
          :aria-busy="translationPending"
        >
          {{ displayNotice }}
        </section>
      </BaseBubble>
      <button
        v-if="
          canTranslate &&
          !showsChinese &&
          (hasVisibleTranslation ||
            translationPending ||
            showTranslationRequest)
        "
        type="button"
        class="email-translation-toggle inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap !rounded-full !border !border-solid !border-n-blue-6 !bg-n-solid-1 px-3 py-1.5 text-sm leading-5 !text-n-blue-11 hover:!bg-n-blue-2 disabled:cursor-wait disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
        :disabled="!hasVisibleTranslation && !canRequestTranslation"
        :aria-expanded="hasVisibleTranslation ? translationExpanded : undefined"
        :aria-busy="translationPending"
        :aria-label="translationExpanded ? '收起翻译' : 'AI翻译为中文'"
        :title="
          hasVisibleTranslation
            ? translationExpanded
              ? '收起翻译'
              : '展开中文翻译'
            : translationRequestLabel
        "
        data-translation-request
        @click.stop="
          hasVisibleTranslation ? toggleTranslation() : translateMessage()
        "
      >
        <span
          :class="
            translationPending
              ? 'i-lucide-loader-circle animate-spin'
              : 'i-lucide-languages'
          "
          class="size-4"
          aria-hidden="true"
        />
        <span>{{ aiLabel }}</span>
      </button>
    </div>
    <EmailOpenSignal v-if="isOutgoing" />
  </div>
</template>

<style lang="scss">
// The AI control always sits beside the bubble's bottom edge, as for chat
// messages; the bubble yields the width instead of wrapping the control.
.email-translation-row {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-end;
  gap: 0.5rem;
  max-width: min(100%, 720px);
}

.email-translation-row > [data-bubble-name='email'] {
  min-width: 0;
}

// Tailwind resets break the rendering of google drive link in Gmail messages
// This fixes it using https://developer.mozilla.org/en-US/docs/Web/CSS/Attribute_selectors

.letter-render [class*='gmail_drive_chip'] {
  box-sizing: initial;
  @apply bg-n-slate-4 border-n-slate-6 rounded-md !important;

  a {
    @apply text-n-slate-12 !important;

    img {
      display: inline-block;
    }
  }
}

// Email clients (Gmail, Outlook) hardcode dir="ltr" on wrapper elements.
// In RTL apps this forces email content LTR regardless of actual text.
[dir='rtl'] .letter-render [dir='ltr'] {
  direction: inherit;
}
</style>
